import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Container from '@material-ui/core/Container';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import RefreshIcon from '@material-ui/icons/Refresh';
import dayjs from 'dayjs';
import api from '../../services/api';
import ContatosLead from '../../components/ContatosLead';
import { CORES, SELOS } from '../Home/styles';
import { intervalo } from '../Home/periodo';

/**
 * Meus leads de hoje (F1 + F2): todos os follow-ups vencidos e de hoje do assessor logado,
 * de todos os mailings, numa lista só. Lê o mesmo endpoint do módulo da Home (GET dashboard/followups),
 * sempre com o assessor logado — não depende do escopo por carteira (fase 6).
 */

const LIMITE = 500; // pedido ao backend; se ele ignorar, vale o limite do módulo da Home

function normalizar(dados) {
    const lista = Array.isArray(dados) ? dados : (Array.isArray(dados?.data) ? dados.data : []);
    const hoje = dayjs().format('YYYY-MM-DD');
    return lista.map((r) => {
        const data = r.follow_up ? String(r.follow_up).slice(0, 10) : '';
        const atrasado = typeof r.atrasado === 'boolean' ? r.atrasado : (data && data < hoje);
        return { ...r, follow_up_data: data, atrasado: !!atrasado };
    });
}

/**
 * Cliente/projeto de cada linha. Usa r.cliente quando o endpoint já devolve;
 * senão monta o mapa mailing → cliente com as mesmas chamadas do board
 * (customers/select/get + mailings?customer_id=), uma por cliente do assessor.
 */
async function mapaMailingCliente(idAssessor) {
    const params = idAssessor ? { assessor_id: idAssessor } : {};
    const { data: clientes } = await api.get('customers/select/get', { params });
    const porId = {};
    const porNome = {};
    await Promise.all((clientes || []).map((c) =>
        api.get('mailings', { params: { ...params, customer_id: c.id } })
            .then(({ data }) => (data?.data || []).forEach((m) => {
                porId[m.id] = c.name;
                if (m.name) porNome[m.name] = c.name;
            }))
            .catch(() => {})
    ));
    return { porId, porNome };
}

function Selo({ tom, children }) {
    return (
        <span style={{ ...SELOS[tom], borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {children}
        </span>
    );
}

function Contador({ rotulo, valor, tom }) {
    return (
        <div style={{ background: '#fff', border: `1px solid ${CORES.borda}`, borderRadius: 12, padding: '12px 16px', minWidth: 150 }}>
            <div style={{ fontSize: 13, color: CORES.textoSuave }}>{rotulo}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: tom || CORES.texto }} data-testid={`contador-${rotulo}`}>{valor}</div>
        </div>
    );
}

export default function MeusLeadsHoje() {
    const [linhas, setLinhas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(false);
    const [busca, setBusca] = useState('');
    const [mailing, setMailing] = useState('');
    const [cliente, setCliente] = useState('');
    const [soAtrasados, setSoAtrasados] = useState(false);
    const [versao, setVersao] = useState(0);

    useEffect(() => {
        let ativo = true;
        setCarregando(true);
        setErro(false);
        const params = { ...intervalo('mes'), limite: LIMITE };
        const idAssessor = localStorage.getItem('id_assessor');
        if (idAssessor) params.assessor_id = idAssessor;
        api.get('dashboard/followups', { params })
            .then(async ({ data }) => {
                let lista = normalizar(data);
                if (lista.length && lista.some((r) => !('cliente' in r))) {
                    try {
                        const mapa = await mapaMailingCliente(idAssessor);
                        lista = lista.map((r) => ({
                            ...r,
                            cliente: r.cliente || mapa.porId[r.mailing_id] || mapa.porNome[r.mailing] || '',
                        }));
                    } catch (e) { /* segue sem o nome do cliente */ }
                }
                if (ativo) setLinhas(lista);
            })
            .catch(() => { if (ativo) { setLinhas([]); setErro(true); } })
            .finally(() => { if (ativo) setCarregando(false); });
        return () => { ativo = false; };
    }, [versao]);

    const mailings = useMemo(
        () => Array.from(new Set(linhas.map((r) => r.mailing).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
        [linhas]
    );

    const clientes = useMemo(
        () => Array.from(new Set(linhas.map((r) => r.cliente).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
        [linhas]
    );

    const visiveis = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return linhas
            .filter((r) => !cliente || r.cliente === cliente)
            .filter((r) => !mailing || r.mailing === mailing)
            .filter((r) => !soAtrasados || r.atrasado)
            .filter((r) => !t || [r.razao_social, r.mailing, r.cliente].some((v) => String(v || '').toLowerCase().includes(t)))
            .sort((a, b) => (a.follow_up_data || '').localeCompare(b.follow_up_data || '')); // mais antigo primeiro
    }, [linhas, busca, cliente, mailing, soAtrasados]);

    const atrasados = linhas.filter((r) => r.atrasado).length;
    const deHoje = linhas.length - atrasados;

    return (
        <Container fixed>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ marginBottom: 4 }}>Meus leads de hoje</h1>
                    <div style={{ color: CORES.textoSuave, fontSize: 14 }}>
                        Follow-ups vencidos e de hoje, de todos os seus mailings · {dayjs().format('DD/MM/YYYY')}
                    </div>
                </div>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => setVersao((v) => v + 1)} style={{ color: CORES.verdeEscuro }}>
                    Atualizar
                </Button>
            </div>

            <div style={{ display: 'flex', gap: 12, margin: '16px 0', flexWrap: 'wrap' }}>
                <Contador rotulo="Atrasados" valor={atrasados} tom={atrasados ? '#A63A1A' : undefined} />
                <Contador rotulo="Para hoje" valor={deHoje} />
                <Contador rotulo="Mailings" valor={mailings.length} />
            </div>

            <div style={{ background: '#fff', border: `1px solid ${CORES.borda}`, borderRadius: 12, padding: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField label="Buscar empresa, cliente ou mailing" value={busca} onChange={(e) => setBusca(e.target.value)} size="small" variant="outlined" style={{ minWidth: 260 }} />
                <TextField select label="Cliente/Projeto" value={cliente} onChange={(e) => setCliente(e.target.value)} size="small" variant="outlined" style={{ minWidth: 220 }}>
                    <MenuItem value="">Todos</MenuItem>
                    {clientes.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                <TextField select label="Mailing" value={mailing} onChange={(e) => setMailing(e.target.value)} size="small" variant="outlined" style={{ minWidth: 220 }}>
                    <MenuItem value="">Todos</MenuItem>
                    {mailings.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
                <FormControlLabel control={<Checkbox checked={soAtrasados} onChange={(e) => setSoAtrasados(e.target.checked)} style={{ color: CORES.verde }} />} label="Só atrasados" />
            </div>

            {carregando && <LinearProgress style={{ marginTop: 12 }} />}
            {erro && <div style={{ color: '#8E2F12', margin: '16px 0' }}>Não foi possível carregar seus follow-ups. Tente Atualizar.</div>}

            {!carregando && !erro && (
                <div style={{ background: '#fff', border: `1px solid ${CORES.borda}`, borderRadius: 12, marginTop: 12, overflow: 'hidden' }}>
                    {visiveis.length === 0 ? (
                        <div style={{ padding: 24, color: CORES.textoSuave }} data-testid="vazio">
                            {linhas.length === 0 ? 'Nenhum follow-up vencido ou para hoje. Bom trabalho!' : 'Nenhum lead com esses filtros.'}
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }} data-testid="tabela-meus-leads">
                            <thead>
                                <tr style={{ background: CORES.fundoSuave, textAlign: 'left', color: CORES.textoSuave }}>
                                    <th style={{ padding: '10px 12px' }}>Follow-up</th>
                                    <th style={{ padding: '10px 12px' }}>Empresa</th>
                                    <th style={{ padding: '10px 12px' }}>Cliente/Projeto</th>
                                    <th style={{ padding: '10px 12px' }}>Mailing · status</th>
                                    <th style={{ padding: '10px 12px' }}>Contato</th>
                                    <th style={{ padding: '10px 12px' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {visiveis.map((r) => (
                                    <tr key={r.mailing_leads_id} style={{ borderTop: `1px solid ${CORES.borda}` }}>
                                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                                            <div>{r.follow_up_data ? dayjs(r.follow_up_data).format('DD/MM/YYYY') : '—'}</div>
                                            <Selo tom={r.atrasado ? 'erro' : 'alerta'}>{r.atrasado ? 'Atrasado' : 'Hoje'}</Selo>
                                        </td>
                                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>{r.razao_social}</td>
                                        <td style={{ padding: '10px 12px' }} data-testid="celula-cliente">{r.cliente || '—'}</td>
                                        <td style={{ padding: '10px 12px', color: CORES.textoSuave }}>{r.mailing}{r.status_rotulo ? ` · ${r.status_rotulo}` : ''}</td>
                                        <td style={{ padding: '10px 12px' }}><ContatosLead leadId={r.lead_id} mailingLeadsId={r.mailing_leads_id} /></td>
                                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                            <Link to={`/leads/${r.lead_id}/edit?mailing_leads_id=${r.mailing_leads_id}`} style={{ color: CORES.verdeEscuro, fontWeight: 600, textDecoration: 'none' }}>
                                                Registrar contato →
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </Container>
    );
}
